import React from "react";
import { useTranslation } from "react-i18next";
import { useMachine } from "@xstate/react";
import { Box } from "@mui/material";
import truncate from "lodash/truncate";
import { InvestmentTypeEnum } from "@api";
import { LoadingProgressBar, PageLayout } from "@components";
import { useMandatorLinks } from "@hooks";
import { InvestmentTypeStep } from "@features/transactionWizard/steps/InvestmentTypeStep";
import { newTransactionWizardSM } from "./stateMachine/newTransactionWizardSM";
import { SearchSymbolStep } from "./steps/SearchSymbolStep";
import { useStateHelpers } from "./hooks/useStateHelpers";
import { NewTransactionSteps } from "./NewTransactionSteps";
import type { Symbol } from "@bn/api/apiv1";

export interface Props {
  tickerSymbol?: string;
  quoteProvider?: string;
  depotId?: number;
}

export const NewTransactionWizard = ({
  tickerSymbol,
  quoteProvider,
  depotId,
}: Props) => {
  const [currentState, sendEvent] = useMachine(newTransactionWizardSM);
  const { isLoadingState } = useStateHelpers(currentState);
  const initFromSearch = !tickerSymbol || !quoteProvider;
  const { t } = useTranslation();
  const { getInstrumentDetailUrl } = useMandatorLinks();
  const investmentValues = currentState.context?.investmentValues;

  const backOneStep = () => sendEvent({ type: "BACK" });

  const breadcrumbs: BreadcrumbItem[] = [
    { to: "/", text: t("breadcrumbs.home") },
    { to: "/investments", text: t("breadcrumbs.investments") },
    { text: t("transactionWizard.new.title") },
  ];
  const securityPageBreadCrumb: BreadcrumbItem = {
    href:
      getInstrumentDetailUrl?.({
        type: investmentValues?.type,
        isin: investmentValues?.isin,
        quoteProvider: investmentValues?.quoteProvider,
      }) ?? undefined,
    text: truncate(investmentValues?.name),
  };

  const shouldShowBreadcrumb =
    investmentValues?.isin && investmentValues?.quoteProvider;

  if (shouldShowBreadcrumb) breadcrumbs.splice(2, 0, securityPageBreadCrumb);

  return (
    <PageLayout breadcrumbs={breadcrumbs}>
      <Box>
        {isLoadingState() && <LoadingProgressBar />}

        {currentState.matches("idle") &&
          initFromSearch &&
          sendEvent({
            type: "INIT_FROM_SYMBOL_SEARCH",
            depotId: depotId,
          }) && <LoadingProgressBar />}

        {currentState.matches("idle") &&
          !initFromSearch &&
          sendEvent({
            type: "INIT_FROM_TICKER",
            quoteProvider: quoteProvider!,
            tickerSymbol: tickerSymbol!,
            depotId: depotId,
          }) && <LoadingProgressBar />}

        {currentState.matches("selectInvestmentType") && (
          <InvestmentTypeStep
            handleNext={(investmentType: InvestmentTypeEnum) => {
              sendEvent({ type: "INVESTMENT_TYPE_SELECTED", investmentType });
            }}
            handlePrevious={() => window.history.back()}
          />
        )}

        {currentState.matches("searchSymbol") && (
          <SearchSymbolStep
            handleNext={(symbol: Symbol | SymbolWithQuote) => {
              sendEvent({ type: "INIT_FROM_SYMBOL", symbol: symbol });
            }}
            handlePrevious={backOneStep}
            type={currentState.context.investmentValues.type}
            depotId={depotId}
          />
        )}

        <NewTransactionSteps
          currentState={currentState}
          sendEvent={sendEvent}
          initFromSearch={initFromSearch}
          depotId={depotId}
        />
      </Box>
    </PageLayout>
  );
};
